import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { useState } from 'react'

const RealTimePOR = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index)
  }

  const faqs = [
    {
      question: "What types of digital asset service providers can benefit from Real-Time Proof of Reserves attestations?",
      answer: "Multiple company types can benefit from real-time transparency reporting, especially those that issue tokens backed by real-world assets. Similarly, those issuing exchange-traded notes backed by on-chain assets can close the transparency gap between on- and off-chain assets with real-time attestation reporting services.",
      bullets: [
        "Stablecoins: where attestation reporting provides transparency to a token that is issued on a blockchain, pegged to a fiat currency, and the backing by assets held in reserve by the issuer's custodian on behalf of token holders.",
        "Asset-Backed Tokens: where attestation reporting provides transparency to a token issued on a blockchain that is pegged/representative of another type of asset or instrument, such as a carbon-credit, partnership interest in real estate, or an ounce of gold, and is backed by assets held in reserve by the issuer's custodian on behalf of token holders.",
        "Exchange Traded Products: where attestation reporting provides transparency to a security instrument issued and backed by digital assets held in reserve by the issuer's custodian on behalf of noteholders."
      ]
    },
    {
      question: "Can I pair Real-Time Reserves with your On-Chain Proof of Reserves Service?",
      answer: "Yes! In fact, it is encouraged to provide real-time transparency to your \"traditional\" users expecting a CPA attestation, along with crypto-native users expecting an On-Chain Proof of Reserves!"
    },
    {
      question: "How long does it take to complete the integration and begin reporting with Real-Time Reserves?",
      answer: "The standard integration timeline is 6 weeks. Integration and reporting start times depend on technical scoping and the attest customer onboarding processes."
    },
    {
      question: "Is it really \"real time\"?",
      answer: "It's more real-time than annual audits, and more real-time than monthly attestations. The technology we utilize for all Real-Time Proof of Reserves services has a 30-second heartbeat and will call APIs and other sources on that cadence.",
      bullets: [
        "The reporting interval (the time between the timestamp of the last attest report and the time a new attest report is available) depends on the least available data source.",
        "Assuming all of the token, bank or other balance information is readily available via API or similar data extraction method, we can reduce the reporting interval to 30 seconds, compared to every 30 days (+ time to issue) for traditional \"static\" stablecoin attestations!",
        "However, if a data source is restricted to providing balance data once an hour, then our reporting interval would be 1 hour (because we are bound by the least available data source).",
        "Most Real-Time Reserve services have a reporting interval between 30 seconds and daily."
      ]
    },
    {
      question: "Will Real-Time Reserves fulfill my reporting requirements?",
      answer: "If your regulator accepts attestation reports under attestation standards promulgated by the AICPA our solution will almost certainly meet your requirements.",
      bullets: [
        "We encourage prospective customers to discuss potential attestation services with their regulators.",
        "We have and will produce additional information regarding our services for regulators' review. Just inquire with us."
      ]
    }
  ]

  return (
    <>
      <Helmet>
        <title>Real-Time Proof of Reserves - Audit Financier</title>
        <meta name="description" content="Transparency reporting at the speed of innovation. Real-time attestation reporting with 24/7 monitoring and instant verification." />
      </Helmet>

      <div className="real-time-por-page">
        {/* Hero Section with Background Image */}
        <section className="relative overflow-hidden bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 text-white">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src="/cpaservices.jpg"
              alt="CPA Services Background"
              className="w-full h-full object-cover"
            />
            {/* Dark overlay for text readability */}
            <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          </div>

          <div className="container-custom relative z-10 py-24 md:py-32">
            <div className="max-w-4xl">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">
                Real-Time<br />
                <span className="text-accent-400">Proof of<br />Reserves</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 font-light">
                Transparency reporting at the speed of innovation.
              </p>
            </div>
          </div>
        </section>

        {/* Speed Comparison Section */}
        <section className="section-padding bg-gray-50">
          <div className="container-custom">
            <div className="flex flex-col md:flex-row items-center justify-between gap-12 mb-16">
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-4xl md:text-5xl font-bold text-accent mb-4">
                  Elevate your<br />
                  Transparency<br />
                  Reporting...
                </h2>
              </div>

              <div className="flex-shrink-0">
                <div className="w-32 h-32 md:w-40 md:h-40 relative">
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    <circle cx="50" cy="50" r="45" fill="none" stroke="#3B82F6" strokeWidth="2" />
                    <circle cx="50" cy="50" r="40" fill="none" stroke="#3B82F6" strokeWidth="1" opacity="0.3" />
                    
                    {/* Clock ticks */}
                    {[...Array(12)].map((_, i) => {
                      const angle = (i * 30 - 90) * (Math.PI / 180)
                      const x1 = 50 + 42 * Math.cos(angle)
                      const y1 = 50 + 42 * Math.sin(angle)
                      const x2 = 50 + 38 * Math.cos(angle)
                      const y2 = 50 + 38 * Math.sin(angle)
                      return (
                        <line
                          key={i}
                          x1={x1}
                          y1={y1}
                          x2={x2}
                          y2={y2}
                          stroke="#3B82F6"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      )
                    })}
                    
                    {/* Hour hand */}
                    <line x1="50" y1="50" x2="50" y2="30" stroke="#1E40AF" strokeWidth="3" strokeLinecap="round" />
                    
                    {/* Minute hand */}
                    <line x1="50" y1="50" x2="50" y2="20" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" />
                    
                    {/* Center dot */}
                    <circle cx="50" cy="50" r="3" fill="#1E40AF" />
                    
                    {/* Top knob */}
                    <rect x="47" y="5" width="6" height="4" rx="2" fill="#3B82F6" />
                    <line x1="50" y1="9" x2="50" y2="15" stroke="#3B82F6" strokeWidth="2" />
                  </svg>
                </div>
              </div>

              <div className="flex-1 text-center md:text-right">
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  <span className="text-accent">From 30 Days<br />to</span><br />
                  <span className="text-accent">30 Seconds!</span>
                </h2>
              </div>
            </div>

            <div className="max-w-5xl mx-auto space-y-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-accent flex items-center justify-center mt-1">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-lg text-gray-700 flex-1">
                  Real-Time Reserves closes the gap between on-chain liabilities and off-chain assets, with on-demand CPA-attestation reporting.
                </p>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-accent flex items-center justify-center mt-1">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-lg text-gray-700 flex-1">
                  Real-Time Proof of Reserves Services reduce your company's operational burdens and saves time.
                </p>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-accent flex items-center justify-center mt-1">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-lg text-gray-700 flex-1">
                  Our services are enabled with proven, market-leading tech that provides key insights for your operations, finance, and custody teams.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What is Real-Time PoR Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 text-white section-padding">
          <div className="container-custom relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 text-accent-400">
              What is Real-Time Proof of Reserves?
            </h2>
            <p className="text-xl text-center text-gray-300 mb-16">
              Tech-enabled Attestation Reporting
            </p>

          </div>
        </section>

        {/* Use Cases Section */}
        <section className="section-padding bg-white">
          <div className="container-custom">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-accent">
              Use Cases for Real-Time Proof of Reserves
            </h2>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Use Case 1 */}
              <div className="text-center group hover:transform hover:scale-105 transition-transform duration-300">
                <div className="w-24 h-24 mx-auto mb-6 bg-accent-100 rounded-full flex items-center justify-center group-hover:bg-accent-200 transition-colors">
                  <svg className="w-12 h-12 text-accent" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">
                  Stablecoin Issuers &<br />Financial Institutions
                </h3>
              </div>

              {/* Use Case 2 */}
              <div className="text-center group hover:transform hover:scale-105 transition-transform duration-300">
                <div className="w-24 h-24 mx-auto mb-6 bg-accent-100 rounded-full flex items-center justify-center group-hover:bg-accent-200 transition-colors">
                  <svg className="w-12 h-12 text-accent" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M3 12v3c0 1.657 3.134 3 7 3s7-1.343 7-3v-3c0 1.657-3.134 3-7 3s-7-1.343-7-3z" />
                    <path d="M3 7v3c0 1.657 3.134 3 7 3s7-1.343 7-3V7c0 1.657-3.134 3-7 3S3 8.657 3 7z" />
                    <path d="M17 5c0 1.657-3.134 3-7 3S3 6.657 3 5s3.134-3 7-3 7 1.343 7 3z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">
                  Asset-Backed Tokens &<br />RWA Tokenizers
                </h3>
              </div>

              {/* Use Case 3 */}
              <div className="text-center group hover:transform hover:scale-105 transition-transform duration-300">
                <div className="w-24 h-24 mx-auto mb-6 bg-accent-100 rounded-full flex items-center justify-center group-hover:bg-accent-200 transition-colors">
                  <svg className="w-12 h-12 text-accent" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">
                  Exchange-Traded Funds &<br />Exchange-Traded Products
                </h3>
              </div>
            </div>
          </div>
        </section>

        {/* Outputs & Live Demo Section */}
        <section className="section-padding bg-gray-50">
          <div className="container-custom">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-accent">
              Real-Time Proof of Reserves Outputs & Live Demo
            </h2>

            <div className="grid lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
              {/* Left Column - Outputs Info */}
              <div className="space-y-8">
                <div className="bg-white rounded-2xl p-8 shadow-soft">
                  <h3 className="text-2xl font-bold mb-6 text-gray-900">
                    1. CPA Attestation Reports under AICPA Standards
                  </h3>
                  <p className="text-gray-700 mb-6">
                    The Audit Financier Team's Proof of Reserves attestation reporting is issued under standards promulgated by the American Institute of Certified Public Accountants (AICPA).
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p className="text-gray-700">
                        The Audit Financier Team customers can make the Independent Accountant's Report available to customers directly (either permissioned or publicly) for on-demand download.
                      </p>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p className="text-gray-700">
                        Under AICPA Standards The Audit Financier Team can provide Agreed Upon Procedures where we provide Procedures and findings over the subject matter, or Examinations where we provide an opinion over management's assertion being fairly stated or the subject matter being in accordance with (or based on) the criteria, in all material respects.
                      </p>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p className="text-gray-700">
                        The same reports used by top issuers are available to users in real-time!
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-8 shadow-soft">
                  <h3 className="text-2xl font-bold mb-6 text-gray-900">
                    2. Embeddable Widget
                  </h3>
                  <p className="text-gray-700 mb-6">
                    The Audit Financier Team's widget enables customers to provide Real-Time Proof of Reserves attestations to interested parties.
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p className="text-gray-700">
                        The widget hosts a dashboard showing on-chain liabilities and off-chain assets, from multiple blockchains, custodians, and financial institutions.
                      </p>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p className="text-gray-700">
                        The widget provides the most recent downloadable attest report as well as historical attestation reporting.
                      </p>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p className="text-gray-700">
                        The Audit Financier Team's service is flexible to any blockchain source and any custodian or financial institution API.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Live Demo */}
              <div className="bg-white rounded-2xl p-6 shadow-soft">
                <h3 className="text-xl font-bold mb-4 text-center">
                  See it Live and in Action Below
                </h3>

                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent flex items-center justify-center mt-0.5">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-gray-700 text-sm">
                      Download the example report below.
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent flex items-center justify-center mt-0.5">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-gray-700 text-sm">
                      View the different tokens/products and see balances.
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent flex items-center justify-center mt-0.5">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-gray-700 text-sm">
                      View the FAQ
                    </p>
                  </div>
                </div>

                {/* Tab Navigation */}
                <div className="flex gap-2 border-b border-gray-200 mb-6">
                  <button className="px-4 py-2 text-sm font-medium text-gray-700 border-b-2 border-accent">
                    Balances
                  </button>
                  <button className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700">
                    Table
                  </button>
                  <button className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700">
                    Historical
                  </button>
                  <button className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700">
                    Ripcord
                  </button>
                  <button className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700">
                    FAQ
                  </button>
                </div>

                {/* Dashboard Content */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <p className="text-sm text-gray-600 mb-6 text-center">
                    Welcome to LedgerInsight, a real-time reserve balance dashboard. You are viewing data for the<br />
                    <span className="font-semibold">Crypto-Collateralized Instruments</span>.
                  </p>

                  {/* Token Display */}
                  <div className="bg-white rounded-lg p-6 shadow-sm mb-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm">₦</span>
                      </div>
                      <span className="text-lg font-semibold">nUSD</span>
                    </div>

                    <div className="text-3xl font-bold text-green-500 mb-6">
                      $ 122,130,435,218
                    </div>

                    {/* Bar Chart */}
                    <div className="relative h-48 flex items-end justify-center gap-8 mb-4">
                      <div className="text-xs text-gray-500 absolute left-0 top-0 flex flex-col justify-between h-full">
                        <span>140B</span>
                        <span>120B</span>
                        <span>100B</span>
                        <span>80B</span>
                        <span>60B</span>
                        <span>40B</span>
                        <span>20B</span>
                        <span>0B</span>
                      </div>
                      <div className="flex-1 ml-12 h-full flex items-end justify-center gap-4">
                        <div className="w-24 bg-green-400 rounded-t" style={{ height: '80%' }}></div>
                        <div className="w-24 bg-gray-900 rounded-t" style={{ height: '95%' }}></div>
                      </div>
                    </div>
                  </div>

                  {/* Token Info */}
                  <div className="bg-white rounded-lg p-4 shadow-sm flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-xs">₦</span>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">June 30 07:45 AM</div>
                        <button className="mt-1 bg-black text-white px-4 py-1 rounded text-sm font-medium hover:bg-gray-800 transition-colors">
                          Download Report
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Integrations Section */}
        <section className="section-padding bg-white">
          <div className="container-custom">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-accent">
              Real-Time Reserves Integrations
            </h2>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Blockchains */}
              <div className="bg-gray-50 rounded-2xl p-8 border-2 border-gray-200 hover:border-accent transition-colors">
                <h3 className="text-2xl font-bold mb-6 text-gray-900">Blockchains</h3>
                <div className="space-y-3">
                  {['Bitcoin', 'Ethereum', 'Litecoin', 'XRP', 'Polkadot', 'Cardano', 'Algorand', 'Solana'].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700 font-medium">{item}</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-accent flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700 font-medium italic">And more!</span>
                  </div>
                </div>
              </div>

              {/* Banks */}
              <div className="bg-gray-50 rounded-2xl p-8 border-2 border-gray-200 hover:border-accent transition-colors">
                <h3 className="text-2xl font-bold mb-6 text-gray-900">Banks</h3>
                <div className="space-y-3">
                  {['First Digital Trust', 'Flowbank', 'Capital Union Bank', 'Customers Bank', 'Fortress Trust', 'Deltec', 'J.P. Morgan Chase', 'Cross River Bank'].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700 font-medium">{item}</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-accent flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700 font-medium italic">And more!</span>
                  </div>
                </div>
              </div>

              {/* Custodians & Exchanges */}
              <div className="bg-gray-50 rounded-2xl p-8 border-2 border-gray-200 hover:border-accent transition-colors">
                <h3 className="text-2xl font-bold mb-6 text-gray-900">Custodians & Exchanges</h3>
                <div className="space-y-3">
                  {['Coinbase', 'Binance.com', 'Kraken', 'Bitgo', 'Komainu', 'OKEX', 'Huobi', 'Bitfinex, Bitstamp, Deribit'].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700 font-medium">{item}</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-accent flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700 font-medium italic">LMAX, B2C2, OSL, Pit, and more!</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Leading at Intersection Section */}
        <section className="section-padding bg-gray-50">
          <div className="container-custom">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 text-accent">
              Leading at the Intersection
            </h2>
            <h3 className="text-3xl md:text-4xl font-bold text-center mb-16 text-accent">
              of Digital Assets and Accounting
            </h3>

            <div className="grid md:grid-cols-3 gap-12 max-w-6xl mx-auto">
              {/* Column 1 */}
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-accent rounded-2xl flex items-center justify-center">
                  <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                    <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">
                  First-Ever Real-Time Attestation by a CPA
                </h3>
                <p className="text-gray-700">
                  The The Audit Financier Team team built the first-ever real-time attestation reporting system, first deployed in 2019. Today our transparency services cover Billions in USD value.
                </p>
              </div>

              {/* Column 2 */}
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-accent rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.736 6.979C9.208 6.193 9.696 6 10 6c.304 0 .792.193 1.264.979a1 1 0 001.715-1.029C12.279 4.784 11.232 4 10 4s-2.279.784-2.979 1.95c-.285.475-.507 1-.67 1.55H6a1 1 0 000 2h.013a9.358 9.358 0 000 1H6a1 1 0 100 2h.351c.163.55.385 1.075.67 1.55C7.721 15.216 8.768 16 10 16s2.279-.784 2.979-1.95a1 1 0 10-1.715-1.029c-.472.786-.96.979-1.264.979-.304 0-.792-.193-1.264-.979a4.265 4.265 0 01-.264-.521H10a1 1 0 100-2H8.017a7.36 7.36 0 010-1H10a1 1 0 100-2H8.472c.08-.185.167-.36.264-.521z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">
                  From Stablecoins to RWAs
                </h3>
                <p className="text-gray-700">
                  As tokenization evolves from simple stablecoins to more complex instruments like tokenized securities and RWAs, The Audit Financier Team is at the forefront of transparency.
                </p>
              </div>

              {/* Column 3 */}
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-accent rounded-2xl flex items-center justify-center transform rotate-45">
                  <div className="transform -rotate-45">
                    <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">
                  From Policy to Practice
                </h3>
                <p className="text-gray-700">
                  In conjunction with industry experts, The Audit Financier Team co-authored The Digital Chamber's{' '}
                  <span className="text-accent underline italic">"Practitioner's Guide to Proof of Reserves"</span>, the most comprehensive guide on PoR.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="section-padding bg-white">
          <div className="container-custom">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-accent">
              Real-Time Reserves FAQ
            </h2>

            <div className="max-w-4xl mx-auto space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="border-2 border-gray-200 rounded-2xl overflow-hidden hover:border-accent transition-colors"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-8 py-6 text-left flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-lg font-semibold text-gray-900 pr-8">
                      {faq.question}
                    </span>
                    <svg
                      className={`w-6 h-6 text-accent flex-shrink-0 transition-transform ${
                        openFaq === index ? 'transform rotate-180' : ''
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {openFaq === index && (
                    <div className="px-8 py-6 bg-gray-50 border-t-2 border-gray-200">
                      <p className="text-gray-700 mb-4">{faq.answer}</p>
                      {faq.bullets && (
                        <div className="space-y-3 mt-4">
                          {faq.bullets.map((bullet, idx) => (
                            <div key={idx} className="flex items-start gap-3">
                              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent flex items-center justify-center mt-0.5">
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                              <p className="text-gray-700 flex-1">{bullet}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="section-padding bg-gray-50">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-accent">
                Learn more about<br />Real-Time Proof of Reserves
              </h2>
              <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
                Audit Financier is an experienced and trusted blockchain auditing company that provides Real-Time PoR attestation services for digital asset service providers. Use the link below to book an introductory meeting with one of our experts.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-10 py-4 rounded-full bg-accent text-white text-lg font-semibold transition-all duration-300 hover:bg-accent-600 hover:shadow-lg transform hover:scale-105"
              >
                TALK TO AN EXPERT
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default RealTimePOR
